import { Button } from '@insify/design-system/components';
import { fetchFromContentful } from '@insify/ui-utils';
import React, { useEffect, useState } from 'react';

import { ExitPopup } from './ExitPopup';
import { mapContentToPopup } from './mapContentToPopup';

import type { ExitPopupProps } from './ExitPopup';
import type { FunnelPopup, Popup, PopupButtonProps } from './types';

type FunnelConfig<T extends string> = {
  [key in T]: {
    exitPopupsCollection: {
      items: (FunnelPopup | null)[];
    };
  };
};

const url = `https://graphql.contentful.com/content/v1/spaces/hu5zbimv1zds`;

const createQuery = (contentfulContentTypeId: string) => `query ($id: String!) {
  ${contentfulContentTypeId}(id: $id) {
    exitPopupsCollection(limit: 25) {
      items {
        __typename
        sys {
          id
        }
        name
        paths
        pathMatchStrategy
        showAfter
        slidesCollection(limit: 25) {
          items {
            title
            description
            image {
              url
              contentType
            }
          }
        }
        animationInterval
        primaryButtonLabel
        primaryButtonTarget
        secondaryButtonLabel
        secondaryButtonTarget
      }
    }
  }
}`;

const getButton =
  (kind: 'primary' | 'secondary') =>
  ({ label, target, onClick }: PopupButtonProps) => (
    <Button kind={kind} trackingId={label} href={target} target='_blank' onClick={onClick}>
      {label}
    </Button>
  );

const PrimaryButton = React.memo(getButton('primary'));
const SecondaryButton = React.memo(getButton('secondary'));

type FunnelExitPopupProps = Pick<ExitPopupProps, 'enabled' | 'path'> & {
  contentfulApiKey: string;
  contentfulContentTypeId: string;
  contentfulEntryId: string;
};

export const FunnelExitPopup: React.FC<FunnelExitPopupProps> = ({
  enabled,
  path,
  contentfulApiKey,
  contentfulContentTypeId,
  contentfulEntryId,
}) => {
  const [popups, setPopups] = useState<Popup[]>([]);

  useEffect(() => {
    void fetchFromContentful<FunnelConfig<typeof contentfulContentTypeId>>(url, contentfulApiKey, {
      query: createQuery(contentfulContentTypeId),
      variables: { id: contentfulEntryId },
    }).then(({ [contentfulContentTypeId]: entry }) => {
      const funnelPopups = entry.exitPopupsCollection.items.filter((item) => !!item) as FunnelPopup[];
      const mappedFunnelPopups = mapContentToPopup(funnelPopups);
      setPopups(mappedFunnelPopups);
    });
  }, []);

  return (
    <ExitPopup
      enabled={enabled}
      path={path}
      popups={popups}
      PrimaryButton={PrimaryButton}
      SecondaryButton={SecondaryButton}
    />
  );
};
